import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { saveData } from 'actions';
import { countErrors } from 'common/formValidation/lib';
import withSpinner from '../withSpinner';
import withTutorial from '../withTutorial';
import { Body, Head, Header, Table, listProvider, withActiveItem } from '../list';
import fullName from '../../common/utils/fullName';
import ErrorMessage from '../ErrorMessage';
import ListItem from './ListItem';
import getValidationByProduct from './getValidationByProduct';

import './SimulationTable.scss';

const NoSimuMessage = () => (
    <ErrorMessage>
        <div>
            Pour créer votre première simulation associée à ce e-RIP, cliquez sur le (+) en bas à droite de cette page.
        </div>
    </ErrorMessage>
);

/**
 * Calcule le errorCount d'une simulation à partir de sa fonction de validation.
 *
 * @param {Object} item - La simulation
 * @param {Object} props - Les props passées au validate (session, etc.)
 * @returns {number}
 */
const computeErrorCount = (item, props) => {
    const validate = getValidationByProduct(item.product);

    if (!validate) {
        return 0;
    }

    const errors = validate(item, props);

    // _error est un message global (chaîne) qui ne doit pas être compté
    return countErrors(errors, ['_error']);
};

const SimulationTable = ({ rip, items, session, updateErrorCount }) => {
    const itemIds = items.map((item) => item.id).join(',');

    useEffect(() => {
        items.forEach((item) => {
            const errorCount = computeErrorCount(item, { session, rip });

            if (item && item.id && errorCount !== item.errorCount) {
                updateErrorCount(item.id, errorCount);
            }
        });
    }, [itemIds]);

    return (
        <Table>
            <Head>
                <th />
                <Header sortProperty="name" text={`Simulations de ${fullName(rip)}`} />
                <Header sortProperty="product" text="Type" />
                <Header sortProperty="createdAt" text="Date de création" />
                <Header sortProperty="updatedAt" text="Dernière modification" />
                {session.needApprovalOnSimulations ? (
                    <Header sortProperty="approval" text="Validation" />
                ) : null}
                <th />
            </Head>
            <Body>
                {items.map((item) => (
                    <ListItem
                        key={item.id}
                        rip={rip}
                        item={item}
                    />
                ))}
            </Body>
        </Table>
    );
};

SimulationTable.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    rip: PropTypes.shape({}).isRequired,
    session: PropTypes.shape({
        needApprovalOnSimulations: PropTypes.bool,
    }).isRequired,
    updateErrorCount: PropTypes.func.isRequired,
};

export default compose(
    connect(null, (dispatch) => ({
        updateErrorCount: (id, errorCount) => dispatch(saveData('simulation', id, { errorCount })),
    })),
    listProvider({
        sorts: {
            createdAt: { initialOrder: 'desc', default: true },
            name: { initialOrder: 'asc' },
        },
        name: 'simulation',
        type: 'simulation',
        filter: ({ session, rip }) => (item) => session.permissions.mayReadSimulation(item, rip),
        criteria: ({ rip: { id } }) => ({ ripId: id }),
    }),
    withSpinner,
    withTutorial(NoSimuMessage),
    withActiveItem,
    pure,
)(SimulationTable);
